import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import ModeCommentIcon from '@mui/icons-material/ModeComment';
import emptyImg from '@/assets/images/emptyState-Graphic.png';
import DialogIntroSequence from '@/components/DialogIntroSequence/DialogIntroSequence';
import DialogNewCR from '@/components/DialogNewCR/DialogNewCR';
import Button from '@/components/UI/Button/Button';
import Spinner from '@/components/UI/Spinner/Spinner';
import * as Const from '@/helpers/constants';
import { isFileOnlyUser, filterPills } from '@/helpers/constants';
import { isSubCRById } from '@/helpers/utils';
import useApiRequest from '@/hooks/useApiRequest';
import useAuth from '@/hooks/useAuth';
import useGlobalSpinnerContext from '@/hooks/useGlobalSpinner';
import useToastContext from '@/hooks/useToastContext';
import { getPillOverview, getPillSubCROverview } from '@/redux/OverviewFilters/overviewFilters.reducer';
import { getSettingsInfo, getUserAll } from '@/redux/User/user.reducer';
import campaignServices from '@/services/campaignServices';
import titleService from '@/services/titleService';
import { Language, Tasks, TasksDto, TileCounter, TileCounterDto, User } from '@/types';
import { AREA_COUNTERS_TILES, TASKS_INFO, TASKS_OPTIONS } from './dashboard-utils';
import NewsDashboard from './NewsDashobard/NewsDashboard';
import StatusSummary from './StatusSummary';
import Tile from './Tile';
import styles from './Dashboard.module.scss';

const ROLES_WITHOUT_CR_OVERVIEW = [Const.CAMPAIGN_MANAGER, Const.COMMITTEE_COORDINATOR, Const.CAMPAIGN_MANAGER_JV,
Const.CART_QMB_PLANT_APPROVER, Const.QMB_PLANT_APPROVER_JV, Const.CART_PLANT_SUPERVISOR, Const.VAN_E3_QMB_PLANT_APPROVER, Const.VAN_CAMPAIGN_MANAGER];
const ROLES_WITHOUT_CR_OVERVIEW_COUNTER = [];

const LinkRow = ({ href, icon, text, lang }) => (
  <a href={Const.URLDashboard[lang]?.[href]} target="_blank" rel="noopener noreferrer" className={styles.ContentWrapper}>
    <i className={`di ${icon} ${styles.CardIcon}`} />
    {text}
  </a>
);

type DashboardComponent = {
  lang: Language;
  pillsFilter?: string;
  pillSubCRFilterState?: string;
  toggleMenu: (fromAdminTile: boolean) => void;
  user: User;
};

const Dashboard: React.FC<DashboardComponent> = (props) => {
  const { lang, pillsFilter, pillSubCRFilterState, toggleMenu } = props;
  const { t } = useTranslation();
  const authService = useAuth();
  const [open, setOpen] = useState(false);
  const [statistics, setStatistics] = useState<TileCounter | null>(null);
  const [tasks, setTasks] = useState<Tasks | null>(null);
  const [openIntro, setOpenIntro] = useState(false);
  const [news, setNews] = useState<any>();
  const [isDisplayLoading, setDisplayLoading] = useState<boolean>(false);
  const [choice, setChoice] = useState<string>(TASKS_OPTIONS.requestsToApprove);
  const [showStatusSummary, setShowStatusSummary] = useState<boolean>(false);
  const { loading, setLoading } = useGlobalSpinnerContext();
  const { addToast } = useToastContext();
  const userWithoutTaskArea = authService?.checkRoles([Const.CENTER_VIEWER, Const.REPORTER, Const.COMMITTEE_COORDINATOR]);
  const showShowIntroSection = authService.checkRoles([Const.CREATOR, Const.CREATOR_JV, Const.CREATOR_EXTERNAL, Const.APPROVER, Const.APPROVER_JV]);

  const isSpecialVANRole = useMemo(() => {
    const selectedVanE2Role = `${Const.CART_ROLE_PREFIX}${Const.VAN_E2_QMB_APPROVER}`;
    const vanE3PlantApproverRole = `${Const.CART_ROLE_PREFIX}${Const.VAN_E3_QMB_PLANT_APPROVER}`;
    const allUserRoles = authService?.getUserRoles() || [];

    return authService?.roleSelected === selectedVanE2Role && allUserRoles.includes(vanE3PlantApproverRole);
  }, [authService]);

  const AREAS = useMemo(() => ({
    campaignOverview: {
      display: statistics?.allRequests,
      show: !authService?.checkRoles(ROLES_WITHOUT_CR_OVERVIEW),
    },
    createNewCr: {
      onClick: () => setOpen(true),
      show: authService?.checkRoles([Const.CREATOR, Const.CREATOR_EXTERNAL, Const.CREATOR_JV, Const.ADMIN]),
    },
    myOpenRequests: {
      display: statistics?.myOpenRequests,
      show: authService?.checkRoles([Const.CREATOR, Const.CREATOR_EXTERNAL, Const.CREATOR_JV]),
    },
    administration: {
      show: authService?.checkRole(Const.ADMIN),
    },
    requestsToApprove: {
      display: statistics?.requestsToApprove,
      show: authService?.checkRoles([Const.APPROVER, Const.APPROVER_JV, Const.VAN_E3_CAUSER, Const.VAN_E3_CHIEF_ENGINEER_CARLINE]),
    },
    subCampaignRequestsToApprove: {
      display: statistics?.requestsToApproveSubCr,
      show: authService?.checkRoles([Const.CART_QMB_PLANT_APPROVER, Const.QMB_PLANT_APPROVER_JV, Const.VAN_E3_QMB_PLANT_APPROVER]),
    },
    subCampaignRequestsToApproveByQMB: {
      display: statistics?.inPlantApprovalSubCr,
      show: authService?.checkRoles([Const.CAMPAIGN_MANAGER, Const.CAMPAIGN_MANAGER_JV]),
    },
    subCampaignOverview: {
      display: statistics?.allRequestsSubCr,
      show: authService?.checkRoles([Const.CAMPAIGN_MANAGER, Const.COMMITTEE_COORDINATOR, Const.CAMPAIGN_MANAGER_JV,
      Const.CART_QMB_PLANT_APPROVER, Const.QMB_PLANT_APPROVER_JV, Const.CART_PLANT_SUPERVISOR, Const.VAN_E3_QMB_PLANT_APPROVER, Const.VAN_CAMPAIGN_MANAGER]),
    },
    applicationsNotAssigned: {
      display: statistics?.notAssignedSubCr,
      show: authService?.checkRoles([Const.CAMPAIGN_MANAGER, Const.VAN_CAMPAIGN_MANAGER, Const.CAMPAIGN_MANAGER_JV]),
    },
    applicationsAssigned: {
      display: statistics?.assignedToMeSubCr,
      show: authService?.checkRoles([Const.CAMPAIGN_MANAGER, Const.VAN_CAMPAIGN_MANAGER, Const.CAMPAIGN_MANAGER_JV]),
    },
    notAssignedToMe: {
      display: statistics?.notAssigned,
      show: authService?.checkRoles([Const.RD_COMMITTEE_COORDINATOR, Const.VAN_COMMITTEE_COORDINATOR]),
    },
    assignedToMe: {
      display: statistics?.assignedToMe,
      show: authService?.checkRoles([Const.RD_COMMITTEE_COORDINATOR, Const.VAN_COMMITTEE_COORDINATOR]),
    },
    requestsToInPlantConsolidation: {
      display: statistics?.inPlantConsolidation,
      show: authService?.checkRoles([Const.COMMITTEE_COORDINATOR]),
    },
    requestsCoordinationToPlantApproval: {
      display: statistics?.coordinationToPlantApproval,
      show: authService?.checkRoles([Const.COMMITTEE_COORDINATOR]),
    },
    requestsToAcknowledge: {
      display: statistics?.requestsToAcknowledge,
      show: authService?.checkRoles([Const.VAN_E2_QMB_APPROVER]),
    },
    requestsToApproveSubCr: {
      display: statistics?.requestsToApproveSubCr,
      show: isSpecialVANRole && authService?.checkRoles([Const.VAN_E2_QMB_APPROVER]),
    },
  }), [statistics, authService, isSpecialVANRole]);

  useEffect(() => {
    titleService.updatePageTitle(t('menu.dashboard'));
  }, [t, lang]);

  const fetchNews = useApiRequest(campaignServices.getNews);

  const leadToSelectedPill = useCallback((area: string) => {
    if (area === 'campaignOverview' && pillsFilter) return `${Const.getAbsoluteRoute(Const.ROUTES.CR_OVERVIEW)}?filter=${pillsFilter}`;
    if (['subCampaignOverview', 'subCampaignOverviewPlantApprovers'].includes(area) && pillSubCRFilterState) return `${Const.getAbsoluteRoute(Const.ROUTES.CR_OVERVIEW)}?filter=${pillSubCRFilterState}`;

    return AREA_COUNTERS_TILES[area]?.link;
  }, [pillsFilter, pillSubCRFilterState]);

  const myAreaTiles = useMemo(() => Object.keys(AREA_COUNTERS_TILES).map((area) => ({
    areaKey: area,
    ...AREA_COUNTERS_TILES[area],
    title: t(AREA_COUNTERS_TILES[area]?.title, { plant: authService?.userPlant }),
    subtitle: t(AREA_COUNTERS_TILES[area]?.subtitle),
    display: AREAS[area]?.display,
    show: AREAS[area]?.show,
    onClick: AREAS[area]?.onClick,
    link: leadToSelectedPill(area),
  })), [AREAS, t, authService, leadToSelectedPill]);

  const availableTaskForUser = useMemo(() => {
    const baseTasks = Object.keys(TASKS_INFO) // tasks to request to the backend
      .filter((taskToShow: string) => authService?.checkRoles(TASKS_INFO[taskToShow].rolesCanSee));

    // Include requestsToApproveSubCr when user is in special VAN role
    if (isSpecialVANRole && !baseTasks.includes(TASKS_OPTIONS.requestsToApproveSubCr)) {
      baseTasks.push(TASKS_OPTIONS.requestsToApproveSubCr);
    }

    return baseTasks;
  }, [authService.roleSelected, isSpecialVANRole]); // eslint-disable-line react-hooks/exhaustive-deps

  const visibleTasksWithContentForUser = useMemo(() => {
    const canSeeSubCr = isSpecialVANRole || authService?.checkRoles(TASKS_INFO[TASKS_OPTIONS.requestsToApproveSubCr].rolesCanSee);

    return availableTaskForUser
      .filter((taskToShow: string) => tasks && tasks?.[taskToShow]?.length > 0)
      .filter((taskToShow: string) => taskToShow !== TASKS_OPTIONS.requestsToApproveSubCr || canSeeSubCr);
  }, [availableTaskForUser, tasks, isSpecialVANRole, authService?.roleSelected]); // eslint-disable-line react-hooks/exhaustive-deps

  const lastLoadKeyRef = useRef<string>('');
  const inFlightRef = useRef<boolean>(false);

  const buildLoadKey = useCallback(() => {
    const role = authService.roleSelected;
    const isSubCrNow = authService?.checkRoles([Const.CART_PLANT_SUPERVISOR, Const.CART_QMB_PLANT_APPROVER, Const.VAN_CAMPAIGN_MANAGER,
    Const.VAN_E3_QMB_PLANT_APPROVER, Const.QMB_PLANT_APPROVER_JV]);

    const userWithoutTaskAreaNow = authService?.checkRoles([Const.CENTER_VIEWER, Const.REPORTER, Const.COMMITTEE_COORDINATOR]);
    const crCounterWithoutRightsNow = authService?.checkRoles(ROLES_WITHOUT_CR_OVERVIEW_COUNTER);
    const isFileOnly = isFileOnlyUser(authService);

    return [`role=${role ?? ''}`, `subcr=${isSubCrNow ? 1 : 0}`, `noTasks=${userWithoutTaskAreaNow ? 1 : 0}`, `noCounters=${crCounterWithoutRightsNow ? 1 : 0}`,
    `fileOnly=${isFileOnly ? 1 : 0}`].join('|');
  }, [authService]);

  const removeAllLoaders = useCallback(() => {
    setLoading(false);
    setDisplayLoading(false);
  }, [setLoading]);

  const loadDashboardData = useCallback(() => {
    const loadKey = buildLoadKey();
    if (loadKey === lastLoadKeyRef.current) return;
    if (inFlightRef.current) return;

    lastLoadKeyRef.current = loadKey;
    inFlightRef.current = true;

    setLoading(true);
    setDisplayLoading(true);

    let counterLoaded = false;
    let taskLoaded = false;

    const currentIsSubCr = authService?.checkRoles([Const.CART_PLANT_SUPERVISOR, Const.CART_QMB_PLANT_APPROVER, Const.VAN_CAMPAIGN_MANAGER,
    Const.VAN_E3_QMB_PLANT_APPROVER, Const.QMB_PLANT_APPROVER_JV]);
    const currentUserWithoutTaskArea = authService?.checkRoles([Const.CENTER_VIEWER, Const.REPORTER, Const.COMMITTEE_COORDINATOR]);

    // NEWS
    fetchNews()
      .then((result) => {
        result?.news?.sort((a, b) => Date.parse(b.publicationDate) - Date.parse(a.publicationDate));
        setNews(result?.news);
      })
      .catch((error) => {
        if (error?.name !== Const.errorTypes.ABORT_ERROR) addToast(error.message, 'ERROR');
      });

    if (isFileOnlyUser(authService)) {
      counterLoaded = true;
      taskLoaded = true;
      removeAllLoaders();
      inFlightRef.current = false;
      setShowStatusSummary(false);

      return;
    }

    const finishIfDone = () => {
      if ((counterLoaded || currentUserWithoutTaskArea) && taskLoaded) {
        removeAllLoaders();
        inFlightRef.current = false;
      }
    };

    const noCounterAreas = ['create-new-cr', 'administration'];
    const countersChipsIds = myAreaTiles
      .filter((area) => area.show && !noCounterAreas.includes(area.id))
      .map((area) => `chips=${area.chipKey}`);

    // Add requestsToApproveSubCr counter when user is in special VAN role
    if (isSpecialVANRole && !countersChipsIds.some((chip) => chip.includes(filterPills.requestsToApproveSubCr))) {
      countersChipsIds.push('chips=requestsToApproveSubCr');
    }

    campaignServices.getCampaignsStatistics(countersChipsIds, !currentIsSubCr)
      .then((newStatistics: TileCounterDto) => setStatistics(newStatistics.counters))
      .catch((error) => {
        if (error?.name !== Const.errorTypes.ABORT_ERROR) addToast(error.message, 'ERROR');
      })
      .finally(() => {
        counterLoaded = true;
        finishIfDone();
      });

    if (!currentUserWithoutTaskArea) {
      const tasksChipsIds = availableTaskForUser
        .map((task) => `chips=${task}`)
        .filter((value, index, self) => self.indexOf(value) === index);

      campaignServices.getTasks(tasksChipsIds, !currentIsSubCr)
        .then((newTasks: TasksDto) => setTasks(newTasks.tasks))
        .catch((error) => {
          if (error?.name !== Const.errorTypes.ABORT_ERROR) addToast(error.message, 'ERROR');
        })
        .finally(() => {
          taskLoaded = true;
          finishIfDone();
        });
    } else {
      taskLoaded = true;
      finishIfDone();
    }

    setShowStatusSummary(false);
  }, [addToast, authService, availableTaskForUser, buildLoadKey, fetchNews, myAreaTiles, removeAllLoaders, setLoading, isSpecialVANRole]);

  useEffect(() => {
    loadDashboardData();
  }, [authService.roleSelected, isSpecialVANRole, loadDashboardData]);

  useEffect(() => {
    const handleRoleChange = () => loadDashboardData();
    window.addEventListener('roleChanged', handleRoleChange);

    return () => window.removeEventListener('roleChanged', handleRoleChange);
  }, [loadDashboardData]);

  useEffect(() => {
    setChoice(visibleTasksWithContentForUser?.[0] || TASKS_OPTIONS.requestsToApprove);
  }, [visibleTasksWithContentForUser]);

  const myTasksChipsOptions = useMemo(() => Object.keys(TASKS_INFO)
    .filter((taskToShow: string) => visibleTasksWithContentForUser.includes(taskToShow))
    .map((chip) => {
      const { chipOption } = TASKS_INFO[chip];
      const isSpecialSubCrApproveTask = chip === TASKS_OPTIONS.requestsToApproveSubCr && isSpecialVANRole;

      return {
        ...chipOption,
        ...(isSpecialSubCrApproveTask && {
          key: 'requests-to-approve-subcr-button',
          id: 'requests-to-approve-subcr-button',
          btnText: 'dashboard.requestsToApproveSubCr',
        }),
        handleClick: () => setChoice(chip),
        buttonStyle: 'GreyFilterDashboard',
        btnText: t(isSpecialSubCrApproveTask ? 'dashboard.requestsToApproveSubCr' : chipOption.btnText),
        active: choice === chip,
        showBadge: {
          isNotification: false,
          badge: tasks?.[chip]?.length,
        },
      };
    }), [visibleTasksWithContentForUser, choice, tasks, t, isSpecialVANRole]);

  const myTaskTiles = useMemo(() => tasks?.[choice]?.map((actualTask, index) => ({
    ...TASKS_INFO[choice],
    title: t(TASKS_INFO[choice]?.title),
    messageTitle: t(TASKS_INFO[choice]?.messageTitle),
    dateMessage: t(TASKS_INFO[choice]?.dateMessage || ''),
    id: `${actualTask?.campaignId}--${index}`,
    link: `/front/${isSubCRById(actualTask?.campaignId) ? 'sub-' : ''}campaign/${actualTask?.campaignId}`,
    subtitle: actualTask?.campaignTitle[lang.toUpperCase()],
    display: actualTask?.comment,
    date: actualTask?.statusDate?.substring(0, 11).replaceAll('-', '/'),
    subCROrCRId: actualTask?.campaignId,
    modelSeries: actualTask?.modelSeries,
  })), [tasks, choice, t, lang]);

  return (
    <div className={styles.Dashboard}>
      <div className={styles.PageContent}>
        <section className={styles.BG} />
        <h1 data-testid="dasboard-welcome">{t('welcome.title')}</h1>
        <div className={styles.Section}>
          <div className={styles.CardHeader}>
            <header>
              <h3 className={styles.CardTitle}>{t('academy.label')}</h3>
            </header>
            {showShowIntroSection && (
              <div id="show-intro-tour" data-testid="show-intro-tour" className={styles.ContentWrapper} onClick={() => setOpenIntro(true)}>
                <i className={`di icon-praesentation ${styles.CardIcon}`} />
                {t('settingsMenu.helpShowIntra')}
              </div>
            )}
            <LinkRow href="base" icon="icon-weltkugel" lang={lang} text={t('academy.information')} />
            <LinkRow href="faq" icon="icon-hilfe" lang={lang} text={t('settingsMenu.faq')} />
            <LinkRow href="learn" icon="icon-datei-vsdx" lang={lang} text={t('academy.training')} />
            <a href={Const.SUPPORT_CHANNEL} target="_blank" rel="noopener noreferrer" className={styles.ContentWrapper}>
              <ModeCommentIcon className={styles.SupportIcon} />
              {t('menu.support')}
            </a>
          </div>
          <div className={styles.CardHeader}>
            <header>
              <i className={`di icon-rss-feed ${styles.NewsfeedTitleIcon}`} />
              <h3 className={styles.CardTitle}>{t('editableFields.label')}</h3>
            </header>
            <div className={styles.Newsfeed}>
              {news?.length > 0
                ? news.map((pnews) => <NewsDashboard news={pnews} key={pnews.id} />)
                : t('editableFields.noNewsDisplay')}
            </div>
          </div>
        </div>
        <h2>{t('dashboard.myAreas')}</h2>
        <div className={styles.Section}>
          {myAreaTiles.map((tileOp): any => (
            <Tile
              {...tileOp}
              {...(tileOp.id === 'administration' ? { onClick: () => toggleMenu(true) } : {})}
              key={tileOp.areaKey}
              isDisplayLoading={isDisplayLoading}
            />
          ))}
        </div>
        <div className={styles.StatusSummary}>
          <span className={styles.AllCounters} onClick={() => setShowStatusSummary(!showStatusSummary)}>
            <p>
              <i className={`di icon-pfeil-chevron-${showStatusSummary ? 'oben' : 'unten'} ${styles.Icon} ${styles.IconArrow} `} />
              {t('dashboard.cartCampaignStatus')}
              <i className={`di icon-pfeil-chevron-${showStatusSummary ? 'oben' : 'unten'} ${styles.Icon} ${styles.IconArrow} `} />
            </p>
          </span>
          {showStatusSummary && <StatusSummary />}
        </div>
        {!userWithoutTaskArea && (
          <>
            <h2>{t('dashboard.myTasks')}</h2>
            <div className={styles.Section}>
              {(visibleTasksWithContentForUser.length === 0 && !loading) && (
                statistics?.length === 0
                  ? <div className={styles.MyTaskLoader}><Spinner isVisible /></div>
                  : (
                    <div className={styles.ContentImg}>
                      <img id="logo" src={emptyImg} alt="Empty task" />
                      <p className={styles.emptyMessage}> {t('errors.emptyTask')} </p>
                    </div>
                  )
              )}
              {(visibleTasksWithContentForUser.length > 0 && !loading) && (
                <>
                  <div className={styles.OptionChips}>
                    {myTasksChipsOptions?.map(({ key, ...chipOption }) => (
                      <Button key={`myTasksChipsOptions-${key}`} {...chipOption} {...Const.propsForNonTypeScriptComponent} />
                    ))}
                  </div>
                  {
                    myTaskTiles?.map((tileOp) => (
                      <Tile {...tileOp} key={tileOp.link} isTask isDisplayLoading={isDisplayLoading} />
                    ))
                  }
                </>
              )}
            </div>
          </>
        )}
      </div>
      {open && <DialogNewCR close={() => setOpen(false)} />}
      {openIntro && <DialogIntroSequence close={() => setOpenIntro(false)} />}
    </div>
  );
};

const mapStateToProps = (state) => ({
  lang: getSettingsInfo(state).language?.toLowerCase() || 'en',
  pillsFilter: getPillOverview(state),
  pillSubCRFilterState: getPillSubCROverview(state),
  user: getUserAll(state).user,
});

export default connect(mapStateToProps)(Dashboard);
